import { Button, CircularProgress, Collapse, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useRef, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../services/api";

const LS_NAME = 'audioMessageRate';

const useStyles = makeStyles((theme) => ({
  transcriptionContainer: {
    marginTop: 6,
    padding: "6px 10px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
    borderRadius: 7,
    borderLeft: `2px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`,
    maxWidth: "100%",
    wordBreak: "break-word",
  },
  transcriptionText: {
    fontSize: "0.78rem",
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontWeight: 300,
    margin: 0,
    whiteSpace: "pre-wrap",
  },
  transcriptionLabel: {
    fontSize: "0.65rem",
    fontWeight: 400,
    color: theme.palette.text.disabled,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    marginBottom: 2,
  },
  transcribeBtn: {
    marginTop: 4,
    fontSize: "0.7rem",
    fontWeight: 400,
    textTransform: "none",
    color: "#25D366",
    padding: "2px 6px",
    minWidth: "auto",
    borderRadius: 12,
    "&:hover": {
      color: "#1da851",
      backgroundColor: theme.palette.type === "dark" ? "rgba(37,211,102,0.1)" : "rgba(37,211,102,0.08)",
    },
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
    padding: "4px 0",
  },
  loadingText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

const AudioModal = ({ url, message }) => {
  const classes = useStyles();
  const audioRef = useRef(null);
  const [audioRate, setAudioRate] = useState(parseFloat(localStorage.getItem(LS_NAME) || "1"));
  const [showButtonRate, setShowButtonRate] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState(message?.transcription || null);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Atualizar transcrição quando a mensagem for atualizada via socket
  useEffect(() => {
    if (message?.transcription && message.transcription !== transcription) {
      setTranscription(message.transcription);
    }
  }, [message?.transcription]);

  useEffect(() => {
    audioRef.current.playbackRate = audioRate;
    localStorage.setItem(LS_NAME, audioRate);
  }, [audioRate]);

  useEffect(() => {
    audioRef.current.onplaying = () => {
      setShowButtonRate(true);
    };
    audioRef.current.onpause = () => {
      setShowButtonRate(false);
    };
    audioRef.current.onended = () => {
      setShowButtonRate(false);
    };
  }, []);

  const toggleRate = () => {
    let newRate = null;

    switch (audioRate) {
      case 0.5:
        newRate = 1;
        break;
      case 1:
        newRate = 1.5;
        break;
      case 1.5:
        newRate = 2;
        break;
      case 2:
        newRate = 0.5;
        break;
      default:
        newRate = 1;
        break;
    }

    setAudioRate(newRate);
  };

  const getAudioSource = () => {
    let sourceUrl = url || "";

    if (isIOS) {
      sourceUrl = sourceUrl.replace(".ogg", ".mp3");
    }

    return (
      <source src={sourceUrl} type={isIOS ? "audio/mp3" : "audio/ogg"} />
    );
  };

  const handleTranscribe = async () => {
    if (!message?.id || transcribing) return;

    setTranscribing(true);
    try {
      const { data } = await api.post(`/messages/${message.id}/transcribe`);
      setTranscription(data.transcription);
    } catch (err) {
      // Extrair mensagem de erro do backend
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Não foi possível transcrever o áudio. Tente novamente.";

      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setTranscribing(false);
    }
  };

  const canTranscribe = message && !message.fromMe && !transcription;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <audio ref={audioRef} controls>
          {getAudioSource()}
        </audio>
        {showButtonRate && (
          <Button
            style={{ marginLeft: "5px", marginTop: "-45px" }}
            onClick={toggleRate}
          >
            {audioRate}x
          </Button>
        )}
      </div>

      {/* Botão transcrever */}
      {canTranscribe && !transcribing && (
        <Button
          className={classes.transcribeBtn}
          onClick={handleTranscribe}
          size="small"
        >
          Transcrever
        </Button>
      )}

      {/* Loading */}
      {transcribing && (
        <div className={classes.loadingContainer}>
          <CircularProgress size={14} />
          <Typography className={classes.loadingText}>
            Transcrevendo áudio...
          </Typography>
        </div>
      )}

      {/* Transcrição */}
      <Collapse in={!!transcription}>
        {transcription && (
          <div className={classes.transcriptionContainer}>
            <div className={classes.transcriptionLabel}>
              Transcrição
            </div>
            <p className={classes.transcriptionText}>
              {transcription}
            </p>
          </div>
        )}
      </Collapse>
    </div>
  );
};

export default AudioModal;